import { Typography } from '@pvh-components/typography';
import { KeyOfMap, WithTestId } from '../../types';
import { toKebabCase } from '../../utils/casing';
import FlagIcon, { FlagId } from '../FlagIcon';
import Spacer from '../Spacer';
import styles from './languageToggle.module.css';

export interface LanguageOption {
  code: string;
  language: string;
  flagId: FlagId;
}

/**
 * Generics are there so that the `defaultLanguage` is restricted to the keys
 * that are passed by the languages dictionary (Map). This is tricky, and uses
 * a custom utility type in order to infer the keys of the dictionary.
 *
 * Keep in mind that we separate the key for a language, from the way we
 * we display the language code. Even though the key and language code might be
 * the same.
 *
 * Important:
 * Please note that you cannot use the generic type C in the interface.
 * Typescript is not able to infer the types correctly. Please use KeyOfMap<C>
 * instead.
 */
export interface LanguageToggleProps<
  C extends string,
  M extends Map<C, LanguageOption>
> extends WithTestId {
  languages: M;
  value?: KeyOfMap<M>;
  onChange?: (language: KeyOfMap<M>) => void;
}

export interface SelectEvent {
  target: {
    value: string;
  };
}

// @todo
// - Adjust clickable area, it is too small.

/**
 * Select component for languages. Will show country code when the select is
 * collapsed.
 *
 * Please note that you cannot use the generic type C in the component.
 * Typescript is not able to infer the types correctly. Please use KeyOfMap<C>
 * instead.
 */
const LanguageToggle = <C extends string, M extends Map<C, LanguageOption>>({
  languages,
  onChange,
  value = languages.keys().next().value,
  dataTestIdPrefix,
}: LanguageToggleProps<C, M>) => {
  if (languages.size === 0) throwNoLanguages();

  const handleChange = ({ target: { value } }: SelectEvent) => {
    if (!languages.has(value as KeyOfMap<M>))
      throwInvalidLanguage(value, Array.from(languages.keys()).toString());

    if (onChange) {
      onChange(value as KeyOfMap<M>);
    }
  };

  // HACK: Typescript compiler is not able to deduce that this value cannot be
  // undefined, because we restrict the 'value' to the keys of the Map that we
  // index with.
  const selected = languages.get(value) as LanguageOption;

  // HACK:
  // The options to customize the native 'select' component are limited.
  // We utilize CSS opacity and absolute positioning to ensure that the
  // component is placed on top off a 'fake' field of the select component. Read
  // more here:
  // https://hadrysmateusz.medium.com/custom-select-component-with-native-behavior-in-react-1b5ddcfacca1
  return (
    <div
      className={styles.languageToggle}
      data-testid={toKebabCase(dataTestIdPrefix, 'languageToggle')}
    >
      <select
        onChange={handleChange}
        value={value}
        data-testid={toKebabCase(dataTestIdPrefix, 'languageToggle', 'select')}
      >
        {Array.from(languages.entries(), ([key, { language }]) => (
          <option key={key} value={key}>
            {language}
          </option>
        ))}
      </select>
      <div className={styles.textWrapper}>
        <FlagIcon width={18} flagId={selected.flagId} />
        <Spacer size="medium" />
        <Typography variant="bodyLarge">
          <u>{selected.code}</u>
        </Typography>
      </div>
    </div>
  );
};

const throwInvalidLanguage = (invalidOption: string, validOptions: string) => {
  throw new Error(
    `handleChange is called with invalid value: ${invalidOption}. It can only be one of these values ${validOptions}`
  );
};

const throwNoLanguages = () => {
  throw new Error('No languages provided, Map object is empty');
};

export default LanguageToggle;
